import Box from '@mui/material/Box';
import { FormErrors } from '@openx/components-core/lib/FormErrors/FormErrors';
import type { PackageFormParams, PackageFormState } from '@openx/types/package';
import { Criteria } from '@openx/types/targeting/targetingValuesTypes';
import { type JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { UseFormSetError } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router';
import { usePackageContext } from '../../utils/context/PackageContext';
import { getInitialPckValues } from './constants';
import { PackageForm } from './PackageForm';
import { getDiff } from './utils/formStateConverter';

export type PackageCreateFormProps = {
  goBack: () => void;
  accountUid: string;
  openXAgentTools?: (isSubmitting: boolean, formValues: PackageFormState) => JSX.Element;
};

export const PackageCreateForm = ({ goBack, accountUid, openXAgentTools }: PackageCreateFormProps) => {
  const { useCreateForm, useRelatedAccountsFetch } = usePackageContext();
  const location = useLocation();
  const navigate = useNavigate();
  const historyStateRef = useRef(location.state);
  const { createPackage, isCreating } = useCreateForm();

  const [submitErrors, setSubmitErrors] = useState<string[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState(accountUid);

  const [resetFormData, setResetFormData] = useState<PackageFormState>();

  const historyAccUid =
    historyStateRef.current?.deal?.account_uid || historyStateRef.current?.brandFloor?.values.account_uid;
  const selectedAccountUid = historyAccUid ? historyAccUid : accountUid;

  const initialValues = useMemo(() => getInitialPckValues(selectedAccountUid), [selectedAccountUid]);

  useEffect(() => {
    if (initialValues && historyStateRef.current?.package) {
      setResetFormData(historyStateRef.current.package);
    }
  }, [initialValues]);

  const backToDealForm = useCallback(
    (nextPckgUid?: string, formValues?: PackageFormState) => {
      const { redirectPath, ...state } = historyStateRef.current;

      const nextState =
        nextPckgUid && formValues
          ? { ...state, deal: { ...state.deal, package_uid: nextPckgUid, targeting: formValues } }
          : state;

      if (redirectPath) {
        navigate(redirectPath, { replace: true, state: nextState });
      }
    },
    [navigate]
  );

  const backToBrandFloorForm = useCallback(
    (state: PackageFormParams) => {
      if (state?.brandFloor?.uid) {
        navigate(`/pricing/floors/${state.brandFloor.uid}/edit`, { replace: true, state });
      } else {
        navigate('/pricing/floors/create', { replace: true, state });
      }
    },
    [navigate]
  );

  const onCancel = useCallback(() => {
    if (historyStateRef.current?.redirectPath) {
      backToDealForm();
    } else if (historyStateRef.current?.brandFloor) {
      backToBrandFloorForm(historyStateRef.current);
    } else {
      goBack();
    }
  }, [backToDealForm, backToBrandFloorForm, goBack]);

  const { relatedAccounts, refetch: refetchRelatedAccounts } = useRelatedAccountsFetch(
    [{ account_uid: selectedAccountId }],
    {
      enabled: false,
    }
  );

  useEffect(() => {
    refetchRelatedAccounts(selectedAccountId);
  }, [refetchRelatedAccounts, selectedAccountId]);

  const onSubmit = useCallback(
    (values: PackageFormState, setError: UseFormSetError<PackageFormState>) => {
      let diff = getDiff(values, initialValues);

      if (!diff.targeting) {
        diff = {
          ...diff,
          targeting: {
            inter_dimension_operator: Criteria.ALL,
            metacategory: initialValues.targeting.metacategory,
          },
        };
      }

      if (!diff.account_uid) {
        diff.account_uid = initialValues.account_uid;
      }

      createPackage({
        backToBrandFloorForm,
        backToDealForm,
        diff,
        historyStateRef,
        initialValues,
        setError,
        setSubmitErrors,
        values,
      });
    },
    [backToBrandFloorForm, backToDealForm, initialValues, createPackage]
  );

  return (
    <Box>
      <FormErrors errors={submitErrors} />
      <PackageForm
        goBack={onCancel}
        initialValues={initialValues}
        currency={relatedAccounts[selectedAccountId]?.currency}
        onSubmit={onSubmit}
        isSubmitting={isCreating}
        setSelectedAccountId={setSelectedAccountId}
        resetFormData={resetFormData}
        openXAgentTools={openXAgentTools}
      />
    </Box>
  );
};
